import { useRef, useState, useEffect } from "react";

/**
 * AumChime - a centered AUM (ॐ) glyph in the footer.
 * Clicking it synthesises a soft 1111 Hz tone (with a gentle 2222 Hz harmonic)
 * using the browser's Web Audio API. No external audio files / no third party.
 */
export default function AumChime() {
  const ctxRef = useRef(null);
  const oscRefs = useRef([]);
  const stopTimerRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const stop = () => {
    try {
      oscRefs.current.forEach((o) => {
        try { o.stop(); } catch (_) { /* osc already stopped */ }
      });
    } catch (_) { /* no-op */ }
    oscRefs.current = [];
    if (stopTimerRef.current) {
      clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
    }
    setPlaying(false);
  };

  useEffect(() => () => stop(), []);

  const play = () => {
    if (playing) {
      stop();
      return;
    }
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = ctxRef.current || new AudioCtx();
      ctxRef.current = ctx;
      if (ctx.state === "suspended") ctx.resume();

      const duration = 5.5; // seconds
      const now = ctx.currentTime;

      // A meditative chord: 432 Hz (natural tuning, grounding) + 888 Hz (abundance) + 1111 Hz (awakening).
      // Volumes balanced so the lowest is most present, the highest is a delicate shimmer.
      const layers = [
        { freq: 432, peak: 0.16, attack: 0.9, release: 1.8 },
        { freq: 888, peak: 0.08, attack: 1.1, release: 1.8 },
        { freq: 1111, peak: 0.07, attack: 1.3, release: 1.8 },
      ];

      const oscList = [];
      layers.forEach(({ freq, peak, attack, release }) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.0001, now);
        gain.gain.exponentialRampToValueAtTime(peak, now + attack);
        gain.gain.setValueAtTime(peak, now + duration - release);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now);
        osc.stop(now + duration + 0.05);
        oscList.push(osc);
      });

      oscRefs.current = oscList;
      setPlaying(true);
      stopTimerRef.current = setTimeout(() => {
        oscRefs.current = [];
        setPlaying(false);
        stopTimerRef.current = null;
      }, duration * 1000 + 200);
    } catch (e) {
      console.warn("Audio playback failed:", e);
    }
  };

  return (
    <button
      type="button"
      onClick={play}
      data-testid="footer-aum"
      aria-label={playing ? "Stop AUM chime" : "Play AUM chime"}
      aria-pressed={playing}
      className="group inline-flex flex-col items-center justify-center select-none focus:outline-none"
    >
      <span
        aria-hidden="true"
        className={`font-serif text-[#C5A059] text-5xl md:text-6xl leading-none transition-all duration-500 ${
          playing ? "animate-pulse drop-shadow-[0_0_18px_rgba(197,160,89,0.55)]" : "group-hover:scale-110 group-hover:text-[#A68545]"
        }`}
      >
        ॐ
      </span>
      <span className="mt-3 text-[0.62rem] tracking-[0.42em] uppercase text-[#5C6670] group-hover:text-[#C5A059] transition-colors">
        {playing ? "Sounding · 1111 Hz" : "AUM · 1111 Hz"}
      </span>
    </button>
  );
}
